import { ConfirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';
import { InputText } from 'primereact/inputtext';
import { Panel } from 'primereact/panel';
import { Info } from 'lucide-react';
import { Dropdown } from 'primereact/dropdown';

import { useConfirmDialogContext } from '../../../../contexts/ConfirmDialog';
import { WidgetTypeOptions } from '../constant/constant';

import CreativeColorPicker from './CreativeColorPicker';

const BasicSection = ({ action, widgetFormik, setHidePreview, loading }) => {
  const { showConfirmationDialog } = useConfirmDialogContext();
  const headerTemplate = () => {
    return (
      <div className="flex align-items-center gap-2 text-sm font-medium text-gray-700">
        <Info className="text-green-500 text-sm w-1rem h-1rem" />
        <span className="text-sm font-bold text-gray-700">
          Widget Core Info
        </span>
      </div>
    );
  };
  const itemTemplate = option => {
    const Icon = option.icon;
    return (
      <div className="flex items-center gap-2">
        <Icon className={`w-1rem h-1rem ${option.color}`} />
        <div className="flex flex-col">
          <span className="text-sm">{option.value}</span>
        </div>
      </div>
    );
  };

  return (
    <Panel header={headerTemplate} className="w-full my-2">
      <ConfirmDialog />
      <div className="grid grid-nogutter w-full gap-2">
        {loading ? (
          <Skeleton className="w-full" height="45px" />
        ) : (
          <div className="col-5 md:col">
            <span className="p-float-label">
              <Dropdown
                showClear
                filter
                value={widgetFormik.values?.widget_type}
                id="chartType"
                options={WidgetTypeOptions}
                optionLabel="value"
                optionValue="key"
                itemTemplate={itemTemplate}
                className="w-full"
                disabled={action === 'view' ? true : false}
                onChange={event => {
                  if (['card', 'gauge'].includes(event?.value)) {
                    widgetFormik.setFieldValue('chart_configuration', {
                      ...widgetFormik.values.chart_configuration,
                      groupBy: [],
                      orderByField: '',
                    });
                  }
                  if (
                    event?.value === 'table-report' &&
                    widgetFormik.touched.chart_configuration
                  ) {
                    showConfirmationDialog({
                      message:
                        'There are some chart configuration changes made do you want to discard them?',
                      header: 'Confirmation',
                      icon: 'pi pi-info-circle',
                      acceptClassName: 'p-button-danger',
                      accept: () => {
                        widgetFormik.setFieldValue(
                          'widget_type',
                          event?.value || ''
                        );
                        widgetFormik.setFieldValue('chart_configuration', {
                          dimension: '',
                          measure: '',
                          measureSummarizedBy: '',
                          measureTarget: 100,
                          groupBy: [],
                          orderByField: '',
                          orderBy: 'asc',
                        });
                      },
                      reject: () => {},
                    });
                  } else {
                    widgetFormik.setFieldValue('custom_properties', {
                      selectedChartTable: 'Project',
                      selectedReportTable: 'Project',
                      reportTablePrimaryKey: 'Project.project_id',
                      chartMeta: null,
                      buttonInfoUrl: '',
                      buttonColor: '',
                      buttonType: '',
                      openIn: { name: 'Same Window', code: 'same-tab' },
                      linkWidget: '',
                      widgetProperty: '',
                      buttonWidgetType: 'tabularWidget',
                      quick_filter_chart_conditions: [
                        { tableField: '', operator: '', compareWithField: '' },
                      ],
                      quick_filter_report_conditions: [
                        { tableField: '', operator: '', compareWithField: '' },
                      ],
                    });
                    widgetFormik.setFieldValue(
                      'widget_type',
                      event?.value || ''
                    );
                  }
                  setHidePreview(true);
                }}
              />
              <label htmlFor="chartType">
                Widget Type <i className="text-red-400">*</i>
              </label>
            </span>
          </div>
        )}

        <div className="col-5 md:col">
          {loading ? (
            <Skeleton className="w-full" height="45px" />
          ) : (
            <span className="p-float-label">
              <InputText
                label="Name"
                className="w-full"
                id="widget_name"
                value={widgetFormik.values?.widget_name}
                onChange={widgetFormik.handleChange}
                required
                disabled={action === 'view' ? true : false}
                inputProps={{
                  maxLength: 150,
                }}
              />
              <label htmlFor="widget_name">
                Widget Name <i className="text-red-400">*</i>
              </label>
            </span>
          )}
        </div>

        <div className="col-5 md:col">
          {loading ? (
            <Skeleton className="w-full" height="45px" />
          ) : (
            <span className="p-float-label">
              <InputText
                label="Description"
                className="w-full"
                id="widget_description"
                value={widgetFormik.values?.widget_description}
                onChange={widgetFormik.handleChange}
                disabled={action === 'view' ? true : false}
                inputProps={{
                  maxLength: 150,
                }}
              />
              <label htmlFor="widget_description">Widget Description</label>
            </span>
          )}
        </div>
      </div>
      <div className="grid grid-nogutter gap-2">
        <div className="col-6 md:col-4 p-0 m-0 mt-2">
          {loading ? (
            <Skeleton className="w-full" height="45px" />
          ) : (
            <CreativeColorPicker
              widgetFormik={widgetFormik}
              action={action}
              colorPickerId="widget_accent_color"
              colorPickerName={'Widget Highlight Color'}
            />
          )}
        </div>
        {['bar-chart', 'line-chart'].includes(
          widgetFormik.values.widget_type
        ) && (
          <div className="col-6 md:col-4 p-0 m-0 mt-2">
            {loading ? (
              <Skeleton className="w-full" height="45px" />
            ) : (
              <CreativeColorPicker
                widgetFormik={widgetFormik}
                action={action}
                colorPickerId="chart_color"
                colorPickerName={'Chart Color'}
              />
            )}
          </div>
        )}
      </div>
    </Panel>
  );
};

export default BasicSection;
